import { useMemo, useState } from 'react';
import { formatServerClock, symbolsOf, useDisplayZone, usePrefsValue, useServerOffset } from '@adiox/ui';
import { useStream } from '../stream/stream';
import { useQuoteSubscription } from '../stream/useQuoteSubscription';
import { normalizeVolume, volumeProblem } from '../trading/tradeRules';
import { useTradeClient } from '../trading/useTrading';
import { effectiveWatchList, spreadPoints, splitPrice } from './marketWatchModel';
import { useDayBar, useSymbols, type SymbolInfo } from './useSymbols';

/** A price in MT5's big-pip style, bigger again for this tile's large Sell/Buy price blocks. */
function BigPrice({ value, digits }: { value: number; digits: number }) {
  const p = splitPrice(value, digits);
  return (
    <span className="num inline-flex items-baseline">
      <span>{p.main}</span>
      <span className="text-[1.5em] font-bold">{p.big}</span>
      <sup className="text-[0.7em]">{p.small}</sup>
    </span>
  );
}

/**
 * One symbol's trading tile: Sell and Buy buttons either side of the volume, the two big prices below (a click trades
 * at the market, right from here), and the day's low, spread/swap and high at the bottom.
 */
function TradeTile({ name, info }: { name: string; info: SymbolInfo }) {
  const quote = useStream((s) => s.quotes[name]);
  const day = useDayBar(name).data ?? null;
  const zone = useDisplayZone();
  const serverOffset = useServerOffset() ?? 0;
  const client = useTradeClient();
  const [volume, setVolume] = useState(String(info.volumeMin));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const step = (direction: 1 | -1) => {
    const v = Number(volume);
    setVolume(
      String(normalizeVolume((Number.isFinite(v) ? v : info.volumeMin) + direction * info.volumeStep, info)),
    );
  };

  const trade = async (side: 'Buy' | 'Sell') => {
    if (!client || busy) return;
    const v = Number(volume);
    const problem = volumeProblem(v, info);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    const outcome = await client.placeOrder({
      symbol: name,
      type: side,
      volume: normalizeVolume(v, info),
      stopLoss: 0,
      takeProfit: 0,
    });
    setBusy(false);
    if (!outcome.ok) setError(outcome.message);
  };

  const high = day ? Math.max(day.h, quote?.bid ?? day.h) : null;
  const low = day ? Math.min(day.l, quote?.bid ?? day.l) : null;
  const spread = quote ? spreadPoints(quote.bid, quote.ask, info.digits) : null;

  return (
    <li className="classic-trade-tile flex flex-col overflow-hidden" data-testid="trading-tile">
      <div className="flex items-center justify-between px-1.5 py-0.5 text-xs">
        <span className="font-semibold">{name}</span>
        <span className="num text-muted">
          {quote ? formatServerClock(quote.t - serverOffset * 60_000, zone.offsetAt) : '--:--:--'}
        </span>
      </div>
      <div className="relative grid grid-cols-[1fr_auto_1fr] items-stretch gap-px">
        <button
          type="button"
          disabled={busy}
          onClick={() => void trade('Sell')}
          aria-label={`Sell ${name} at the market`}
          className="flex flex-col items-stretch disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="py-0.5 text-center text-xs font-bold mt5-sell-text">SELL</span>
          <span className="mt5-sell flex-1 py-1 text-center text-white">
            {quote ? <BigPrice value={quote.bid} digits={info.digits} /> : <span className="num">—</span>}
          </span>
        </button>
        <div className="z-10 flex shrink-0 self-center justify-self-center px-1">
          <div className="classic-trade-vol-group">
            <input
              aria-label={`Volume for ${name}`}
              className="classic-trade-vol num"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
            />
            <div className="classic-trade-spin-col flex flex-col">
              <button
                type="button"
                aria-label={`Increase volume for ${name}`}
                className="classic-trade-spin"
                onClick={() => step(1)}
              >
                ▲
              </button>
              <button
                type="button"
                aria-label={`Decrease volume for ${name}`}
                className="classic-trade-spin"
                onClick={() => step(-1)}
              >
                ▼
              </button>
            </div>
          </div>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => void trade('Buy')}
          aria-label={`Buy ${name} at the market`}
          className="flex flex-col items-stretch disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="py-0.5 text-center text-xs font-bold mt5-buy-text">BUY</span>
          <span className="mt5-buy flex-1 py-1 text-center text-white">
            {quote ? <BigPrice value={quote.ask} digits={info.digits} /> : <span className="num">—</span>}
          </span>
        </button>
      </div>
      <div className="flex items-start justify-between gap-1 border-t border-line/60 px-1.5 py-1 text-[10px] text-muted">
        <span className="text-left">
          LOW
          <br />
          <span className="num text-fg">{low === null ? '—' : low.toFixed(info.digits)}</span>
        </span>
        <span className="text-center">
          Spread: {spread ?? '—'}
          <br />
          Swap: {info.swapLong.toFixed(2)}/{info.swapShort.toFixed(2)}
        </span>
        <span className="text-right">
          HIGH
          <br />
          <span className="num text-fg">{high === null ? '—' : high.toFixed(info.digits)}</span>
        </span>
      </div>
      {error && (
        <p role="alert" className="px-1.5 pb-1 text-[10px] text-down">
          {error}
        </p>
      )}
    </li>
  );
}

/** Market Watch's Trading view: every symbol in Market Watch, tradable right from its own tile, MT5-style. */
export function TradingTiles() {
  const symbols = useSymbols().data;
  const saved = usePrefsValue((s) => s.data.marketWatch);
  const list = useMemo(
    () => effectiveWatchList(symbolsOf({ marketWatch: saved }), symbols ?? []),
    [saved, symbols],
  );
  useQuoteSubscription(list);
  return (
    <ul className="grid h-full auto-rows-min grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-2 overflow-auto p-2">
      {list.map((name) => {
        const info = symbols?.find((s) => s.name === name);
        return info ? <TradeTile key={name} name={name} info={info} /> : null;
      })}
    </ul>
  );
}
